import { FC, useCallback, useContext, useEffect, useRef, useState } from "react";
import { InfinitySpin } from "react-loader-spinner";
import PageNotFound from "../utils/pageNoFound";
import { SelfContext } from "./SelfContext";

interface IRoleGuardProps {
  requiredPermission?: string;
  requiredActions?: Array<keyof PermissionDetail | undefined>;
  children: React.ReactNode;
  isShowPermissionDenies?: boolean;
  isSuperAdmin?: boolean;
}

interface PermissionDetail {
  isRead: boolean;
  isCreate: boolean;
  isUpdate: boolean;
  isDelete: boolean;
}

interface Permission {
  [key: string]: PermissionDetail;
}

export const RoleGuard: FC<IRoleGuardProps> = ({
  requiredPermission,
  requiredActions = [],
  children,
  isShowPermissionDenies = true,
  isSuperAdmin = false,
}) => {
  const { user, isFetched, isLoading, onSyncSelf } = useContext(SelfContext);
  const [hasPermission, setHasPermission] = useState<boolean | undefined>(undefined);
  const [isCheckingPermission, setIsCheckingPermission] = useState(true);

  useEffect(() => {
    if (!isFetched) onSyncSelf();
  }, [isFetched, onSyncSelf]);

  const shouldFetchPermission = useRef(true);
  const syncUserData = useCallback(async () => {
    if (hasPermission === undefined) {
      if (isSuperAdmin && user?.role && user.role.isSuperAdmin === true) {
        setHasPermission(true);
        setIsCheckingPermission(false);
        shouldFetchPermission.current = false;
        return;
      }

      // If not using superAdmin check or user is not superAdmin, check regular permissions
      if (!requiredPermission) {
        setIsCheckingPermission(false);
        return;
      }

      const permissions = (user?.role?.permission as Permission) || {};
      const permissionDetails = permissions[requiredPermission] || {};
      const hasAllPermissions = requiredActions?.every(
        (action: keyof PermissionDetail | undefined) =>
          action ? permissionDetails[action] : false,
      );
      setHasPermission(hasAllPermissions);
      setIsCheckingPermission(false);
      shouldFetchPermission.current = false;
    }
  }, [hasPermission, requiredActions, requiredPermission, user?.role, isSuperAdmin]);
  useEffect(() => {
    if (isFetched) {
      syncUserData();
    }
  }, [isFetched, syncUserData]);

  if (isLoading || !user || isCheckingPermission) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <InfinitySpin color="blue" />
      </div>
    );
  }

  // If no permission is required, allow access
  if (!requiredPermission && !isSuperAdmin) {
    return <>{children}</>;
  }
  if (hasPermission) {
    return <>{children}</>;
  }
  // If superAdmin check is enabled and user is explicitly a superAdmin (strictly true)
  else if (isSuperAdmin && user?.role && user.role.isSuperAdmin === true) {
    console.log("Allowing access due to superAdmin privileges");
    return <>{children}</>;
  } else {
    return isShowPermissionDenies ? <PageNotFound /> : null;
  }
};
